import { map } from "bluebird";
import { mixed, number, object } from "yup";
import { type AppContext } from "~/lib/context";
import { Events, Listeners } from "~/lib/events";
import { makeSingletonKey } from "~/lib/jobQueueSingletonKey";
import { BaseJobDataSchema } from "~/lib/queue/baseJobDataSchema";
import { QueueJobName } from "~/lib/queue/queueJobName";
import { sendJob } from "~/lib/queue/sendJob";
import { type AuthenticatedUser } from "~/lib/session";
import { getKeys, type YupOutputType } from "~/lib/utils";
import { fetchAuthenticatedUser } from "~/services/auth/fetchAuthenticatedUser";

export type EventName = keyof typeof Events;

export type EventPayload<T extends EventName> = YupOutputType<(typeof Events)[T]> & {
  onBehalfOfUser: AuthenticatedUser;
};

export type Listeners = {
  [K in EventName]: ((ctx: AppContext, data: EventPayload<K>) => Promise<void>)[];
};

const HandleEventJobDataSchema = BaseJobDataSchema.concat(
  object({
    event: mixed<EventName>().oneOf(getKeys(Events)).required(),
    onBehalfOfUserId: number().required(),
  })
);

export type HandleEventJobData = YupOutputType<typeof HandleEventJobDataSchema>;

export const handleEventWorkerService = async (ctx: AppContext, data: HandleEventJobData) => {
  const { event } = HandleEventJobDataSchema.validateSync(data, { abortEarly: false });

  const listeners = Listeners[event];
  if (listeners.length === 0) return;

  const onBehalfOfUser = await fetchAuthenticatedUser(ctx, { userId: data.onBehalfOfUserId });

  const payload = {
    ...Events[event].validateSync(data, { abortEarly: false }),
    onBehalfOfUser,
  };

  await map(listeners, (listener) => listener(ctx, payload as Parameters<typeof listener>[1]));
};

export const queueEvent = async <T extends EventName>(
  ctx: AppContext,
  event: T,
  data: YupOutputType<(typeof Events)[T]>
) => {
  if (!ctx.user) return;

  if (Listeners[event].length === 0) return;

  const companyId = ctx.user.companyId;

  await sendJob(ctx, {
    jobName: QueueJobName.EVENT,
    data: { ...data, companyId, event, onBehalfOfUserId: ctx.user.id },
    options: {
      singletonKey: makeSingletonKey({
        for: { companyId, jobName: QueueJobName.EVENT },
        with: { event },
      }),
    },
  });
};
